import { ref, readonly } from 'vue'
import Keycloak from 'keycloak-js'
import { resumableSession } from '../lib/authSession.js'
import { clearAuthReloadAttempts, shouldAttemptLogin, clearLoginAttempts } from '../lib/reloadGuard.js'
import { reportAuthFailure, instrumentFetch } from '../lib/authDiagnostics.js'
import Cookies from 'js-cookie'
import { setFeatureFlags, clearFeatureFlags } from './useFeatureFlag.js'

const keycloakUrl = import.meta.env.VITE_KEYCLOAK_URL || '/keycloak.json'
const keycloak = typeof keycloakUrl === 'string' && keycloakUrl.startsWith('http')
  ? new Keycloak({
      url: keycloakUrl,
      realm: import.meta.env.VITE_KEYCLOAK_REALM || 'DemandSphere-stg',
      clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'ginza_app',
    })
  : new Keycloak(keycloakUrl)
const authenticated = ref(false)
const user = ref(null)
const token = ref(null)
const initialized = ref(false)

// Token refresh
keycloak.onTokenExpired = () => {
  keycloak.updateToken()
}

keycloak.onAuthRefreshSuccess = () => {
  syncToken()
  // Re-sync the offline refresh token to the backend on every refresh so
  // workers always have the freshest credential. Pattern Z (see
  // docs/ds-api-client-refactor.md). Fire-and-forget; transient failures are
  // recovered on the next refresh tick.
  pushKeycloakTokenToBackend()
}

keycloak.onAuthError = () => {
  Cookies.remove('access_token')
  localStorage.removeItem('refresh_token')
  keycloak.logout()
}

// Force a refresh from outside this composable. api/client.js calls it when a
// request comes back 401: a long analysis can outlive the access token, and the
// old behaviour (reload the page to re-login) threw away whatever was on screen
// mid-edit. Returns true when a fresh token is in the cookie.
export async function refreshAccessToken() {
  if (!keycloak.authenticated) return false
  try {
    await keycloak.updateToken(-1) // -1 = refresh regardless of remaining life
    syncToken()
    return Boolean(keycloak.token)
  } catch {
    return false
  }
}

function syncToken() {
  token.value = keycloak.token
  Cookies.set('access_token', keycloak.token, { expires: 7, sameSite: 'lax' })
  localStorage.setItem('refresh_token', keycloak.refreshToken)
}

// POSTs the current Keycloak refresh + access token to the backend so
// background workers can call DS API on the user's behalf when no request
// thread is active (Pattern Z: scheduled runs, webhooks, sub-agent fan-out).
//
// Fire-and-forget. We do not block login on the result — the next refresh
// tick or the next user action retries. The backend persists the refresh
// token encrypted (MessageEncryptor); we deliberately do not store the
// access token in localStorage past keycloak-js's own memory.
async function pushKeycloakTokenToBackend() {
  if (!keycloak.refreshToken || !keycloak.token) return
  try {
    await fetch('/agents/api/v1/me/keycloak_token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${keycloak.token}`,
      },
      body: JSON.stringify({
        refresh_token: keycloak.refreshToken,
        access_token: keycloak.token,
        access_expires_in: keycloak.tokenParsed?.exp
          ? Math.max(0, keycloak.tokenParsed.exp - Math.floor(Date.now() / 1000))
          : null,
        refresh_expires_in: keycloak.refreshTokenParsed?.exp
          ? Math.max(0, keycloak.refreshTokenParsed.exp - Math.floor(Date.now() / 1000))
          : null,
      }),
    })
  } catch {
    // Non-fatal — retry happens on next token refresh.
  }
}

export function useAuth() {
  async function init() {
    if (initialized.value) return

    // skip_auth e2e mode: bypass Keycloak entirely, populate a fake vikinglord
    // user so router guards (e.g. /vikinglord/*) resolve. Backend mirrors this
    // via the X-Skip-Auth header path in app/controllers/concerns/keycloak_auth.rb,
    // which sets @current_user to the same fake test-user-vikinglord identity.
    // Hostname-gated: only honored on localhost / 127.0.0.1 / e2e-web-* (the
    // hosts Jenkins uses for Playwright runs). On real staging/production
    // hostnames, stale skip_auth localStorage from earlier local dev sessions
    // is ignored — same security stance as 50e02e2 but applied here as well.
    const hostname = typeof window !== 'undefined' ? window.location.hostname : ''
    const isDev = hostname === 'localhost' || hostname === '127.0.0.1' || /^e2e-web-/.test(hostname)
    const skipAuth = isDev && typeof localStorage !== 'undefined' && localStorage.getItem('skip_auth') === 'true'
    if (skipAuth) {
      authenticated.value = true
      user.value = {
        sub: 'test-user',
        email: 'test@example.com',
        name: 'Test User',
        roles: ['admin'],
        vikinglord: true,
      }
      // THE SELECTED ACCOUNT HAS TO COME FROM THE SERVER, EVEN HERE.
      //
      // This object used to be the whole story, with no selected_account_id on
      // it. `useResearchAccount` reads `user.selected_account_id`, finds
      // undefined, concludes the selection is invalid, falls back to
      // accounts[0] -- the alphabetically first account -- and SAVES that over
      // whatever was chosen. So in bypass mode the stored preference was never
      // honoured and was actively overwritten on every page load.
      //
      // That made any test pairing API-created data with the UI depend on the
      // fixture account happening to sort first: five planning tests and the
      // projects test failed with "element not found" while the records existed
      // in an account the page was not looking at.
      //
      // Awaited, because loadAccounts races it otherwise and the fallback wins.
      try {
        const me = await fetchMe()
        if (me) {
          user.value = { ...user.value, sub: me.sub || user.value.sub,
                         selected_account_id: me.selected_account_id }
        }
      } catch { /* the bypass must still work with /me unavailable */ }
      // E2E skip_auth: enable every flag so existing tests for Tensor /
      // ProseVector / Workspace / etc. still pass without per-test setup.
      // Real backend gates are still enforced server-side when X-Skip-Auth
      // hits the FeatureGate concern with an @account that has flags off,
      // but most e2e tests use accounts/sites where flags should be ON.
      // An EXPLICIT list, not "everything on". Some flags are omitted on
      // purpose: context.spec.js asserts that Context bounces when its flag is
      // off, so turning it on here deletes that test's subject. Add a key only
      // when e2e needs that UI visible.
      const allOn = {}
      ;['agents', 'prompt_volume', 'prompt_research', 'tensor', 'prosevector',
        'prosevector_batch', 'workspace', 'multi_llm', 'forms_node', 'classifier_node', 'api_console',
        'data_room', 'performance_dashboard', 'projects', 'planning',
        // `research` is the umbrella for the /research hub itself: without it the
        // router bounces research-home and the top-nav link goes nowhere, even
        // though every tool inside is enabled.
        'research', 'trends_explorer'].forEach(k => { allOn[k] = true })
      setFeatureFlags(allOn)
      initialized.value = true
      return
    }

    let restoreFetch = () => {}
    try {
      // Only resume a session we hold BOTH halves of, and only while the
      // access token is still valid. The two halves are scoped differently:
      // localStorage is per-ORIGIN (port included), cookies are per-HOST (port
      // ignored). A second instance on another port therefore inherits the
      // first one's access_token cookie with no refresh token of its own, and
      // keycloak-js cannot recover from that. See lib/authSession.js.
      const resumable = resumableSession({
        token: Cookies.get('access_token'),
        refreshToken: localStorage.getItem('refresh_token'),
      })

      // Once the login redirect has been spent, stop redirecting. 'check-sso'
      // returns instead of navigating, so the app mounts and the failure
      // becomes visible rather than becoming another bounce.
      const store = typeof sessionStorage !== 'undefined' ? sessionStorage : null
      const mayRedirect = shouldAttemptLogin(store)

      restoreFetch = instrumentFetch()
      const auth = await keycloak.init({
        ...(resumable || {}),
        onLoad: mayRedirect ? 'login-required' : 'check-sso',
        checkLoginIframe: false,
        // Request offline_access so the token Keycloak issues stays valid
        // for background workers (scheduled agents, sub-agent fan-out,
        // webhooks). Pattern Z (docs/ds-api-client-refactor.md). Monika
        // confirmed both realms have Offline Session Idle = 30 days and no
        // session max — so as long as ds-agents refreshes at least once a
        // month, the token survives indefinitely.
        scope: 'openid offline_access',
      })

      authenticated.value = auth
      if (auth) {
        // A real login means any earlier failures are history; a later genuine
        // token expiry deserves a full reload budget rather than a spent one.
        try {
          sessionStorage.removeItem('auth_init_error')
        } catch { /* storage unavailable */ }
        clearAuthReloadAttempts(typeof sessionStorage !== 'undefined' ? sessionStorage : null)
        clearLoginAttempts(typeof sessionStorage !== 'undefined' ? sessionStorage : null)
        syncToken()
        // Hand the refresh token to the backend so workers can mint user
        // access tokens for background DS API calls. Fire-and-forget.
        pushKeycloakTokenToBackend()
        user.value = {
          sub: keycloak.tokenParsed?.sub,
          name: keycloak.tokenParsed?.name || keycloak.tokenParsed?.preferred_username,
          email: keycloak.tokenParsed?.email,
          roles: keycloak.tokenParsed?.realm_access?.roles || [],
          vikinglord: false,
        }
        // Fetch vikinglord status from backend (server-verified via DS API)
        await fetchVikinglord()
      }
    } catch (e) {
      console.error('[Auth] Keycloak init failed:', e)
      // Stash it where a reload cannot erase it. The console is wiped by the
      // next navigation, so without this the only copy of the reason is gone
      // before anyone reads it -- which is exactly what made this class of
      // failure so hard to diagnose.
      try {
        sessionStorage.setItem('auth_init_error', e?.message || String(e))
      } catch {
        // Storage unavailable; the console line above is all we get.
      }
      // In development, send it to the server too. A loop that returns to the
      // login screen wipes the console on every navigation, so the only copy of
      // the reason can be gone before anyone reads it. The server log does not
      // navigate.
      reportAuthFailure(e)
    } finally {
      restoreFetch()
      initialized.value = true
    }
  }

  async function fetchVikinglord() {
    try {
      const data = await fetchMe()
      if (data) {
        user.value = {
          ...user.value,
          vikinglord: data.vikinglord === true,
          selected_account_id: data.selected_account_id,
          background_runs: data.background_runs || { status: 'never_connected' },
        }
        // Populate feature flag composable from /me response.
        // `data.features` is the effective map for the user's selected account.
        setFeatureFlags(data.features || {})
      }
    } catch (e) {
      // Non-critical — vikinglord defaults to false; flags remain at empty {}
    }
  }

  async function fetchMe() {
    const headers = { 'Accept': 'application/json' }
    const t = Cookies.get('access_token')
    const skipAuth = typeof localStorage !== 'undefined' && localStorage.getItem('skip_auth') === 'true'
    if (skipAuth) {
      headers['X-Skip-Auth'] = 'playwright'
      headers['X-E2E-Test'] = 'true'  // bypass rack-attack rate-limit safelist
    } else if (t) {
      headers['Authorization'] = `Bearer ${t}`
    }
    const res = await fetch('/agents/api/v1/me', { headers })
    if (!res.ok) return null
    return await res.json()
  }

  // Refreshes only the background_runs field on the authUser ref. Called by
  // the header dropdown after Disconnect / Reconnect so the badge updates
  // without re-fetching feature flags.
  async function refreshBackgroundRunsStatus() {
    const data = await fetchMe()
    if (!data) return
    user.value = {
      ...user.value,
      background_runs: data.background_runs || { status: 'never_connected' },
    }
  }

  // User-initiated revocation from the header dropdown. Calls
  // DELETE /me/keycloak_token; backend marks the row revoked. Fire-and-forget
  // errors are swallowed so the UI doesn't get wedged — the caller refreshes
  // status after this resolves anyway.
  async function disconnectBackgroundRuns() {
    if (!keycloak.token) return
    try {
      await fetch('/agents/api/v1/me/keycloak_token', {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${keycloak.token}` },
      })
    } catch {
      // swallowed
    }
  }

  // User-initiated reconnect after revocation. Forces a Keycloak token
  // refresh (which re-fires our token-push hook via onAuthRefreshSuccess),
  // and as a belt-and-suspenders also pushes whatever's currently in memory.
  async function reconnectBackgroundRuns() {
    try {
      await keycloak.updateToken(-1)
    } catch {
      // fall through to direct push
    }
    await pushKeycloakTokenToBackend()
  }

  function logout() {
    Cookies.remove('access_token')
    localStorage.removeItem('refresh_token')
    clearFeatureFlags()
    // Revoke the backend-stored refresh token so scheduled agents stop
    // running on this user's behalf when they sign out. Fire-and-forget
    // (the Keycloak logout we kick off next won't block on this).
    revokeKeycloakTokenOnBackend().finally(() => keycloak.logout())
  }

  async function revokeKeycloakTokenOnBackend() {
    if (!keycloak.token) return
    try {
      await fetch('/agents/api/v1/me/keycloak_token', {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${keycloak.token}` },
      })
    } catch {
      // Non-fatal on logout — Keycloak session revocation is the source of truth.
    }
  }

  async function getToken() {
    try {
      await keycloak.updateToken(30) // refresh if expires within 30s
      syncToken()
      return keycloak.token
    } catch {
      keycloak.login()
      return null
    }
  }

  return {
    authenticated: readonly(authenticated),
    user: readonly(user),
    token: readonly(token),
    initialized: readonly(initialized),
    init,
    logout,
    getToken,
    refreshBackgroundRunsStatus,
    disconnectBackgroundRuns,
    reconnectBackgroundRuns,
    keycloak,
  }
}
